import { useState } from 'react'
import { Button, Stack, Box, Typography, Collapse } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import Grid from '@mui/material/Grid'
import type { TEnumItem, TPublisherRequest } from '@modules/types'
import { CART_ITEM_TYPE, distributionLineName, formatApiDate } from '@modules/api-core'
import { FilePreview } from '@modules/file-preview'
import { StatedFields } from '@ds/components'
import { formatRetailPrice } from '../../utils/formatRetailPrice'
import { REQUEST_DETAILS_STRINGS } from './strings'
import { LyricsDistributionDetails } from './LyricsDistributionDetails'

interface RequestFieldProps {
    label: string
    value: string | number | null | undefined
    size?: 4 | 6 | 12
}

const RequestField = ({ label, value, size = 6 }: RequestFieldProps) => (
    <Grid size={size}>
        <Typography sx={{ m: 0, mb: 0.5, fontSize: 13, color: 'text.secondary' }}>
            {label}
        </Typography>
        <Typography sx={{ m: 0, fontSize: 17 }}>{value || '-'}</Typography>
    </Grid>
)

export type TRequestDetailsProps = {
    request: TPublisherRequest
    enums: TEnumItem[]
    getEnumNameById: (enums: TEnumItem[], enumName: string, id: number) => string
}

const SHOWN_ABOVE = [
    'licenseeName',
    'distributionMethod',
    'publicationTitle',
    'distributionTerritory',
    'publisherName',
    'publicationType',
    'publicationAuthorArrangerEditor',
    'isReprint',
    'plannedPublicationDate',
    'additionalComments',
    'publicationDescription',
    'isAcademicJournalOrMagazine',
    'createdAt',
    'distributionDetails',
    'files',
    'songs',
    'region',
]

export const RequestDetails = ({
    request,
    enums,
    getEnumNameById,
}: TRequestDetailsProps) => {
    const [showAdditionalInfo, setShowAdditionalInfo] = useState(false)

    if (!request) return null

    const { clientRequest, files } = request
    const {
        licenseeName,
        distributionMethod,
        publicationTitle,
        distributionTerritory,
        publisherName,
        publicationType,
        publicationAuthorArrangerEditor,
        isReprint,
        plannedPublicationDate,
        additionalComments,
        cartItemTypeId,
        publicationDescription,
        isAcademicJournalOrMagazine,
        distributionDetails,
    } = clientRequest

    const isLyricRequest = cartItemTypeId === CART_ITEM_TYPE.LYRICS_ONLY
    const firstLine = distributionDetails?.[0]

    return (
        <Box
            display="flex"
            flexDirection="column"
            height="100%"
            bgcolor="background.paper"
        >
            <Box display="flex" alignItems="center" px={3.5} pt={3.5} pb={3}>
                <Typography
                    component="h2"
                    sx={{ m: 0, fontSize: 26, fontWeight: 500 }}
                    flexGrow={1}
                >
                    {REQUEST_DETAILS_STRINGS.title}
                </Typography>
            </Box>
            <Stack sx={{ overflowY: 'auto', flex: 1 }} pb={1}>
                <Box px={3.5}>
                    <Grid container rowSpacing={2.5} columnSpacing={2}>
                        <RequestField
                            label={REQUEST_DETAILS_STRINGS.licenseeName}
                            value={licenseeName}
                        />
                        {!isLyricRequest && (
                            <RequestField
                                label={REQUEST_DETAILS_STRINGS.distributionMethod}
                                value={
                                    firstLine
                                        ? distributionLineName(firstLine)
                                        : getEnumNameById(
                                              enums,
                                              'request_distribution_method_enum',
                                              distributionMethod ?? 0,
                                          )
                                }
                            />
                        )}
                        <RequestField
                            label={REQUEST_DETAILS_STRINGS.publicationTitle}
                            value={publicationTitle}
                        />
                        {isLyricRequest && (
                            <>
                                <RequestField
                                    label={REQUEST_DETAILS_STRINGS.publicationDescription}
                                    value={publicationDescription || REQUEST_DETAILS_STRINGS.nA}
                                />
                                <RequestField
                                    label={REQUEST_DETAILS_STRINGS.isAcademicJournalOrMagazine}
                                    value={
                                        isAcademicJournalOrMagazine === 1
                                            ? REQUEST_DETAILS_STRINGS.yes
                                            : REQUEST_DETAILS_STRINGS.no
                                    }
                                />
                            </>
                        )}
                        <RequestField
                            label={REQUEST_DETAILS_STRINGS.distributionTerritory}
                            value={distributionTerritory}
                        />
                        <RequestField
                            label={REQUEST_DETAILS_STRINGS.publisher}
                            value={publisherName}
                        />
                        {!isLyricRequest && (
                            <RequestField
                                label={REQUEST_DETAILS_STRINGS.publicationType}
                                value={publicationType || firstLine?.publicationType}
                            />
                        )}
                        <RequestField
                            label={
                                isLyricRequest
                                    ? REQUEST_DETAILS_STRINGS.publicationAuthorArrangerEditor
                                    : REQUEST_DETAILS_STRINGS.arrangerEditorPublication
                            }
                            value={publicationAuthorArrangerEditor || REQUEST_DETAILS_STRINGS.nA}
                        />
                        <Button
                            onClick={() => setShowAdditionalInfo(!showAdditionalInfo)}
                            variant="text"
                            endIcon={showAdditionalInfo ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 400,
                                fontSize: 14,
                                color: 'text.secondary',
                                '&:hover': {
                                    backgroundColor: 'action.hover',
                                },
                                height: 35,
                                ml: -1,
                            }}
                        >
                            {REQUEST_DETAILS_STRINGS.additionalInfo}
                        </Button>
                    </Grid>

                    <Box mt={2}>
                        <Collapse in={showAdditionalInfo}>
                            <Box mt={1}>
                                <Grid container spacing={2}>
                                    <RequestField
                                        label={REQUEST_DETAILS_STRINGS.dateCreated}
                                        value={
                                            request.createdAt
                                                ? formatApiDate(request.createdAt)
                                                : null
                                        }
                                    />
                                    <RequestField
                                        label={REQUEST_DETAILS_STRINGS.reprint}
                                        value={
                                            isReprint === 1
                                                ? REQUEST_DETAILS_STRINGS.yes
                                                : REQUEST_DETAILS_STRINGS.no
                                        }
                                    />
                                    <RequestField
                                        label={REQUEST_DETAILS_STRINGS.plannedPublicationDate}
                                        value={plannedPublicationDate}
                                    />
                                </Grid>

                                <Box mt={2}>
                                    <StatedFields data={clientRequest} skip={SHOWN_ABOVE} />
                                </Box>
                            </Box>
                        </Collapse>
                    </Box>
                </Box>

                {isLyricRequest && distributionDetails && (
                    <LyricsDistributionDetails distributionDetails={distributionDetails} />
                )}

                {additionalComments && (
                    <Box px={3.5} pt={2.5}>
                        <Typography sx={{ m: 0, mb: 0.5, fontSize: 13, color: 'text.secondary' }}>
                            {REQUEST_DETAILS_STRINGS.comments}
                        </Typography>
                        <Typography sx={{ m: 0, fontSize: 17 }}>{additionalComments}</Typography>
                    </Box>
                )}

                {!isLyricRequest && distributionDetails && distributionDetails.length > 0 && (
                    <Box px={3.5} pt={4}>
                        <Typography
                            component="h3"
                            sx={{ m: 0, mb: 3, fontSize: 22, fontWeight: 500 }}
                        >
                            {REQUEST_DETAILS_STRINGS.componentList}
                        </Typography>
                        <Stack spacing={1.5}>
                            {distributionDetails.map(line => (
                                <Box key={line.id} px={2.5} py={2.25} bgcolor="background.default">
                                    <Grid container spacing={1.5}>
                                        <RequestField
                                            label={REQUEST_DETAILS_STRINGS.productType}
                                            value={distributionLineName(line)}
                                            size={4}
                                        />
                                        <RequestField
                                            label={REQUEST_DETAILS_STRINGS.numberOfUnits}
                                            value={line.numberOfUnits?.toLocaleString('en-US')}
                                            size={4}
                                        />
                                        <RequestField
                                            label={REQUEST_DETAILS_STRINGS.netRetailPrice}
                                            value={formatRetailPrice(line.retailPrice, line.currency, enums)}
                                            size={4}
                                        />
                                        <RequestField
                                            label={REQUEST_DETAILS_STRINGS.numberOfSongs}
                                            value={line.totalNumberOfWorks}
                                            size={4}
                                        />
                                        <RequestField
                                            label={REQUEST_DETAILS_STRINGS.numberOfLicensedSongs}
                                            value={line.numberOfCopyrightedWorks}
                                            size={4}
                                        />
                                    </Grid>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                )}
                <Box px={3.5} pt={4}>
                    <Typography
                        component="h3"
                        sx={{ m: 0, mb: 3, fontSize: 22, fontWeight: 500 }}
                    >
                        {REQUEST_DETAILS_STRINGS.files} ({files?.length ?? 0})
                    </Typography>
                    <Stack spacing={2}>
                        {files?.map(file => (
                            <FilePreview key={file.id} file={file} requestId={request.id} />
                        ))}
                    </Stack>
                </Box>
            </Stack>
        </Box>
    )
}
